import React, { useState, createContext, useEffect, useContext } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import axios from "axios";
import { ThemeProvider, Spinner } from "@material-tailwind/react";
import { PublicRoutes } from "./Routes/PublicRoutes";
import DashboardRoutes from "./Routes/DashboardRoutes";
import Login from "./Pages/Public/Login";

// 1. Create context to share auth state across the app
export const AuthContext = createContext();

// 2. PrivateRoute handles protected routes
const PrivateRoute = ({ children }) => {
    const { isAuthenticated, loading, checkAuth } = useContext(AuthContext);

    // 3. Trigger auth check if state is unknown
    useEffect(() => {
        if (isAuthenticated === null) {
            checkAuth();
        }
    }, [isAuthenticated, checkAuth]);

    // 4. Show spinner while auth check is in progress
    if (loading || isAuthenticated === null) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Spinner color="amber" className="h-32 w-32" />
            </div>
        );
    }

    // 5. Redirect to login if not authenticated
    if (!isAuthenticated) return <Navigate to="/login" />;

    // 6. Render protected content
    return children;
};

const App = () => {
    // 7. Track login state
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    // 8. Track loading state
    const [loading, setLoading] = useState(false);

    // 9. Check if user is logged in
    const checkAuth = async () => {
        setLoading(true);
        try {
            await axios.get("/api/admins/auth-check", { withCredentials: true });
            setIsAuthenticated(true);
        } catch {
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    // 10. Login function
    const login = async (username, password) => {
        try {
            await axios.post("/api/admins/login", { username, password }, { withCredentials: true });
            setIsAuthenticated(true);
            return true;
        } catch (error) {
            console.error("Login failed:", error.response?.data);
            return false;
        }
    };

    // 11. Logout function
    const logout = async () => {
        try {
            await axios.post("/api/admins/logout", {}, { withCredentials: true });
            setIsAuthenticated(false);
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout, loading, checkAuth }}>
            <ThemeProvider>
                <Router>
                    <Routes>
                        {/* 12. Login page: redirect if already logged in */}
                        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />

                        {/* 13. Protected dashboard routes */}
                        <Route
                            path="/dashboard/*"
                            element={
                                <PrivateRoute>
                                    <DashboardRoutes />
                                </PrivateRoute>
                            }
                        />

                        {/* 14. Public routes */}
                        <Route path="/*" element={<PublicRoutes />} />
                    </Routes>
                </Router>
            </ThemeProvider>
        </AuthContext.Provider>
    );
};

export default App;
